// ── MCA Companies (Tables) ───────────────────────────────────────────────────
//
// The list MCA Monitor matches bank activity against (dbo.MCACompanies), and
// each company's aliases (dbo.MCAAliases) — the short forms funders actually
// put in the ACH Company Name field.
//
// Companies are never deleted: findings point at them. Switch one off with
// Active, or stop it matching with Matching. After a change, a Full Rescan on
// MCA Monitor rebuilds every finding nobody has decided yet.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, localFilter } from "../../../shared/format";
import { Btn, Card, ErrorMsg, FInput, FSelect, Field, Modal, SearchBar, SectionHeader, Spinner, STh, Td, TdNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

const TYPES = ["MCA", "Filing Agent", "Generic"];
const chip = (bg, fg) => ({ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap", background: bg, color: fg, display: "inline-block" });

export const MCACompaniesPage = () => {
  const { api, role } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [showOff, setShowOff] = useState(false);
  const [edit, setEdit] = useState(null);           // company row, or {} for new
  const [scanMsg, setScanMsg] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = () => { setErr(null); api.get("getMCACompanies").then(setData).catch(e => setErr(e.message)); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const aliasesOf = id => (data && data.aliases || []).filter(a => a.mcacompanyid === id);
  const rows = data ? data.companies
    .filter(c => showOff || c.isactive)
    .map(c => ({ ...c, aliastext: aliasesOf(c.id).map(a => a.alias).join(", "), aliascount: aliasesOf(c.id).length })) : [];
  const filtered = localFilter(rows, q, ["companyname", "aliastext", "email", "phonenumber", "notes", "entitytype"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, "companyname", "asc");
  const H = ({ label, k, numeric }) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric={numeric} />;

  const fullRescan = async () => {
    setScanning(true); setScanMsg(null);
    try {
      const r = await api.post("runMCAScan", { full: true });
      setScanMsg(`Rescanned ${Number(r.txnsscanned || 0).toLocaleString()} transactions · ${r.newfindings || 0} findings rebuilt or new.`);
      load();
    } catch (e) { setScanMsg("Rescan failed: " + e.message); }
    setScanning(false);
  };

  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!data) return <Spinner />;

  return <>
    <SectionHeader title="MCA Companies" count={sorted.length} actions={[
      <SearchBar key="q" value={q} onChange={setQ} placeholder="Name, alias, email…" />,
      <label key="off" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
        <input type="checkbox" checked={showOff} onChange={e => setShowOff(e.target.checked)} /> Show inactive
      </label>,
      <Btn key="x" onClick={() => exportCSV(sorted, ["companyname", "entitytype", "matchenabled", "isactive", "aliastext", "email", "phonenumber", "notes", "findings"], "mca_companies.csv")}>Export</Btn>,
      role.isAdmin && <Btn key="scan" disabled={scanning} onClick={fullRescan}
        title="Rescan every transaction against the list as it stands now. Findings already decided are kept.">{scanning ? "Rescanning…" : "Full Rescan"}</Btn>,
      role.canEdit && <Btn key="add" variant="primary" onClick={() => setEdit({})}>＋ Add MCA</Btn>,
    ].filter(Boolean)} />
    {scanMsg && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>{scanMsg}</div>}

    <Card style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <H label="Company" k="companyname" />
          <H label="Type" k="entitytype" />
          <H label="Matching" k="matchenabled" />
          <H label="Aliases" k="aliascount" numeric />
          <H label="Findings" k="findings" numeric />
          <H label="Email" k="email" />
          <H label="Phone" k="phonenumber" />
          <H label="Notes" k="notes" />
        </tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", fontSize: 13, color: "var(--color-text-secondary)" }}>No companies match.</td></tr>}
          {sorted.map(c => (
            <tr key={c.id} style={{ cursor: role.canEdit ? "pointer" : "default", opacity: c.isactive ? 1 : 0.5 }}
                onClick={() => role.canEdit && setEdit(c)}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
              <Td style={{ fontWeight: 500 }}>{c.companyname}
                {aliasesOf(c.id).length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
                  {aliasesOf(c.id).map(a => <span key={a.id} title={a.source === "learned" ? `Learned from a finding · ${a.createdby || ""}` : `Added by ${a.createdby || ""}`}
                    style={chip(a.source === "learned" ? "#E6F7FC" : "#F1EFE8", a.source === "learned" ? B.cyanDark : "#5F5E5A")}>{a.alias}</span>)}
                </div>}
              </Td>
              <Td>{c.entitytype}</Td>
              <Td>{c.matchenabled ? <span style={chip("#E1F5EE", "#0F6E56")}>On</span> : <span style={chip("#F1EFE8", "#888780")}>Off</span>}</Td>
              <TdNum>{c.aliascount || "—"}</TdNum>
              <TdNum>{c.findings ? <span title={`${c.openfindings} open`}>{c.findings}{c.openfindings ? <span style={{ color: "#993C1D" }}> ({c.openfindings} open)</span> : null}</span> : "—"}</TdNum>
              <Td style={{ fontSize: 12 }}>{c.email || "—"}</Td>
              <Td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{c.phonenumber || "—"}</Td>
              <Td style={{ fontSize: 12, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.notes || ""}>{c.notes || ""}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8 }}>
      Matched against borrower bank activity on MCA Monitor. Aliases in blue were learned from acknowledged findings; grey were typed in.
      After adding or changing companies, run a Full Rescan so existing transactions are checked against the new list.
    </div>

    {edit && <CompanyModal company={edit} aliases={edit.id ? aliasesOf(edit.id) : []}
                           onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} onAliasChange={load} />}
  </>;
};

const CompanyModal = ({ company, aliases, onClose, onSaved, onAliasChange }) => {
  const { api } = useApp();
  const isNew = !company.id;
  const [f, setF] = useState({
    companyname: company.companyname || "", email: company.email || "", phonenumber: company.phonenumber || "",
    entitytype: company.entitytype || "MCA", matchenabled: company.matchenabled ?? true, isactive: company.isactive ?? true,
    notes: company.notes || "",
  });
  const [alias, setAlias] = useState("");
  const [list, setList] = useState(aliases);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(null);
    try { await api.post("saveMCACompany", { id: company.id, ...f }); onSaved(); }
    catch (e) { setErr(e.message); }
    setSaving(false);
  };
  const addAlias = async () => {
    if (!alias.trim()) return;
    setErr(null);
    try {
      const r = await api.post("saveMCAAlias", { add: { mcacompanyid: company.id, alias, source: "manual" } });
      if (r && r.alias) setList(l => l.some(a => a.id === r.alias.id) ? l : [...l, r.alias]);
      setAlias(""); onAliasChange();
    } catch (e) { setErr(e.message); }
  };
  const removeAlias = async id => {
    setErr(null);
    try { await api.post("saveMCAAlias", { remove: id }); setList(l => l.filter(a => a.id !== id)); onAliasChange(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <Modal title={isNew ? "Add MCA company" : company.companyname} onClose={onClose}>
      <Field label="Company name" required><FInput value={f.companyname} onChange={v => set("companyname", v)} placeholder="As the funder names itself" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Email"><FInput value={f.email} onChange={v => set("email", v)} /></Field>
        <Field label="Phone"><FInput value={f.phonenumber} onChange={v => set("phonenumber", v)} /></Field>
      </div>
      <Field label="Type"><FSelect value={f.entitytype} onChange={v => set("entitytype", v)} options={TYPES} /></Field>
      <div style={{ display: "flex", gap: 18, marginBottom: 12, fontSize: 13 }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={!!f.matchenabled} onChange={e => set("matchenabled", e.target.checked)} /> Match against bank activity
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={!!f.isactive} onChange={e => set("isactive", e.target.checked)} /> Active
        </label>
      </div>
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", margin: "-6px 0 12px" }}>
        Only type <b>MCA</b> is matched against bank activity. Filing Agent (CSC, CT Corp) is for UCC searches; Generic is never matched.
      </div>
      <Field label="Notes">
        <textarea value={f.notes} onChange={e => set("notes", e.target.value)} rows={2}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999", fontFamily: "inherit" }} />
      </Field>

      {!isNew && <div style={{ borderTop: "1px solid " + B.cyanLight, paddingTop: 12, marginTop: 4, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: B.cyanDark, marginBottom: 6 }}>Aliases</div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 8 }}>
          What this funder puts in the ACH Company Name field when it is not the legal name — e.g. <i>CFGMS</i>, <i>PARKSIDE FUND</i>.
          Matched as whole words and scored High. At least 4 letters.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {list.length === 0 && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>None yet.</span>}
          {list.map(a => (
            <span key={a.id} style={{ ...chip(a.source === "learned" ? "#E6F7FC" : "#F1EFE8", a.source === "learned" ? B.cyanDark : "#5F5E5A"), display: "inline-flex", gap: 6, alignItems: "center" }}
                  title={a.source === "learned" ? "Learned from an acknowledged finding" : "Added by hand"}>
              {a.alias}<span onClick={() => removeAlias(a.id)} style={{ cursor: "pointer", fontWeight: 700 }}>×</span>
            </span>))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ flex: 1 }}><FInput value={alias} onChange={setAlias} placeholder="Add an alias…" /></div>
          <Btn onClick={addAlias} disabled={!alias.trim()}>Add</Btn>
        </div>
      </div>}
      {isNew && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 12 }}>Save first, then open it again to add aliases.</div>}

      {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving || !f.companyname.trim()} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
      </div>
    </Modal>
  );
};
